/**
 * Persian strings and lookup maps for the traceability inspector panel.
 *
 * Every map is keyed by the raw value the API sends (commodity key, rule code,
 * stage code, source name, unit). Unknown values are reported during
 * development and use a Persian fallback in the visitor-facing UI.
 *
 * Alert sentences are not sent by the API: it returns a stable `message_code`
 * plus raw `message_params`, and `renderMessage` fills the template below. An
 * unknown code uses a Persian fallback instead of exposing the payload message.
 *
 * Stage labels are keyed by the stage `code` (stable across chains); `name` is
 * display-only on the server side.
 */

import { formatNumber, persianDigits } from '@/features/reports/lib/format';
import { getPersianLabel, persianDisplayText } from '@/lib/persianUi';

const STAGE_PARAMS = ['from_stage', 'to_stage', 'stage', 'previous_stage'];

export const fa = {
    inspectorTabs: {
        label: 'نمای پنل بازرس',
        dashboard: 'داشبورد زنده',
        imports: 'واردات',
        balance: 'توازن عرضه و تقاضا',
        geo: 'توزیع به تفکیک استان',
    },

    page: {
        eyebrow: 'ردیابی کالا',
        title: 'پنل بازرسی زنجیره تأمین',
        asOf: (jalali) => `داده‌ها تا ${jalali}`,
        loading: 'در حال بارگذاری…',
        error: 'بارگذاری داده‌های داشبورد ناموفق بود.',
    },

    commodities: {
        red_meat: 'گوشت قرمز وارداتی',
        poultry: 'مرغ',
        oil: 'روغن',
    },
    originCountries: {
        Brazil: 'برزیل',
        India: 'هند',
        Uruguay: 'اروگوئه',
        Pakistan: 'پاکستان',
        Ukraine: 'اوکراین',
        Malaysia: 'مالزی',
    },

    imports: {
        title: 'حجم واردات',
        description: 'مقدار محموله‌ها به تفکیک وضعیت گمرکی',
        product: 'محصول',
        country: 'کشور مبدأ',
        allCountries: 'همهٔ کشورها',
        granularity: 'بازهٔ زمانی',
        week: 'هفتگی',
        month: 'ماهانه',
        year: 'سالانه',
        fromDate: 'از تاریخ',
        toDate: 'تا تاریخ',
        chartTitle: 'روند حجم واردات',
        tableTitle: 'جزئیات دوره‌ای',
        noData: 'برای این فیلترها داده‌ای وجود ندارد.',
        error: 'بارگذاری داده‌های واردات ناموفق بود.',
        amount: 'مقدار',
        growth: 'رشد',
        period: 'دوره',
        start: 'شروع',
        end: 'پایان',
        kg: 'کیلوگرم',
        head: 'رأس',
        products: {
            beef: 'گوشت گاو',
            lamb: 'گوشت گوسفند',
            oil: 'روغن',
            live_animals: 'دام زنده',
        },

        statuses: {
            registered: 'ثبت سفارش',
            at_customs: 'در گمرک',
            released: 'ترخیص‌شده',
        },
    },

    geo: {
        title: 'توزیع به تفکیک استان',
        description: 'مقدار توزیع‌شدهٔ هر محصول به تفکیک استان و دوره',
        product: 'محصول',
        products: {
            beef: 'گوشت گاو',
            lamb: 'گوشت گوسفند',
            poultry: 'مرغ',
            oil: 'روغن',
        },
        granularity: 'بازهٔ زمانی',
        fromDate: 'از تاریخ',
        toDate: 'تا تاریخ',
        dateRange: 'بازهٔ داده‌ها',
        totalDistribution: 'مجموع مقدار توزیع‌شده',
        mapTitle: 'نقشهٔ استان‌ها',
        mapLabel: 'نقشهٔ قابل انتخاب استان‌های ایران بر اساس مقدار توزیع',
        mapLoading: 'در حال بارگذاری نقشهٔ استان‌ها…',
        mapError: 'بارگذاری نقشهٔ استان‌ها ناموفق بود.',
        rankedTitle: 'استان‌ها به‌ترتیب مقدار توزیع',
        heatmapTitle: 'مقدار توزیع استان‌ها در هر دوره',
        provinceReport: 'گزارش استانی',
        cityReport: 'گزارش شهری',
        province: 'استان',
        city: 'شهر / مقصد',
        volume: 'مقدار',
        records: 'تعداد رکورد',
        absoluteKg: 'مقدار مطلق · کیلوگرم',
        kgUnit: 'کیلوگرم',
        kg: (value) => `${formatNumber(value)} کیلوگرم`,
        unassigned: 'تخصیص‌نیافته',
        emptyDestination: 'بدون مقصد',
        exportCsv: 'دریافت فایل جدولی',
        showAll: 'نمایش همه',
        topTen: 'نمایش ۱۰ استان برتر',
        clearProvince: 'پاک‌کردن فیلتر استان',
        low: (value) => `کم · ${value} کیلوگرم`,
        high: (value) => `زیاد · ${value} کیلوگرم`,
        share: (value) => `سهم ${value}`,
        hideEmpty: 'پنهان‌کردن استان‌های بدون داده',
        noRecords: 'برای این بازه داده‌ای وجود ندارد.',
        noCityData: 'منبع این محصول اطلاعات شهر را ثبت نمی‌کند.',
        noCityRecords: 'برای این فیلتر، مقصد شهری ثبت نشده است.',
        noGeographicData: 'دادهٔ جغرافیایی در دسترس نیست',
        oilReason: 'در مدل روغن، مقصد استانی ثبت نمی‌شود.',
        error: 'بارگذاری داده‌های توزیع استانی ناموفق بود.',
    },

    balance: {
        title: 'توازن عرضه و تقاضا',
        description: 'مقایسهٔ عرضهٔ بالادستی با فروش به بازار، همراه با روند قیمت و موجودی',
        product: 'محصول',
        products: {
            beef: 'گوشت گاو',
            lamb: 'گوشت گوسفند',
            chicken: 'مرغ',
            sunflower_oil: 'روغن آفتابگردان',
        },
        granularity: 'بازهٔ زمانی',
        week: 'هفتگی',
        month: 'ماهانه',
        year: 'سالانه',
        fromDate: 'از تاریخ',
        toDate: 'تا تاریخ',
        scaleTitle: 'توازن عرضه و فروش به بازار',
        scaleLabel: 'ترازوی عرضه و فروش به بازار؛ عرضه در سمت راست و فروش در سمت چپ',
        stateBadges: {
            balanced: 'متعادل',
            surplus: 'مازاد عرضه',
            shortage: 'فشار کمبود',
            unavailable: 'دادهٔ این بازه موجود نیست',
        },
        balancedBandLabel: 'محدودهٔ توازن ±۱۰٪',
        trendTitle: 'روند عرضه، فروش، موجودی و قیمت',
        trendNote: 'مقدارها بر حسب کیلوگرم و قیمت‌ها به تومان برای هر کیلوگرم نمایش داده شده‌اند.',
        supply: 'عرضه',
        sales: 'فروش به بازار',
        stock: 'موجودی تجمعی',
        index: 'شاخص توازن',
        noSalesIndex: 'بدون فروش ثبت‌شده',
        factoryPrice: 'میانگین قیمت فروش',
        retailPrice: 'میانگین قیمت خرده‌فروشی',
        retailDirection: 'جهت قیمت خرده‌فروشی',
        kg: 'کیلوگرم',
        toman: 'تومان/کیلوگرم',
        priceDirections: {
            rising: 'رو به افزایش',
            falling: 'رو به کاهش',
            flat: 'تقریباً ثابت',
            unavailable: 'جهت نامشخص',
        },
        priceMissing: 'قیمت ثبت نشده',
        noPeriodData: 'دادهٔ این بازه موجود نیست',
        verdicts: {
            surplus: (percent, stock) => `عرضه ${percent}٪ بیشتر از فروش است؛ ${stock}`,
            shortage: (percent, stock) => `فروش ${percent}٪ بیشتر از عرضه است؛ ${stock}`,
            balanced: (stock) => `عرضه و فروش به بازار تقریباً برابرند؛ ${stock}`,
            noSales: (supply) => `فروش به بازار در این بازه ثبت نشده و عرضه ${supply} کیلوگرم بوده است؛ موجودی در حال افزایش است`,
            noSupply: (sales) => `عرضه در این بازه ثبت نشده و ${sales} کیلوگرم فروش ثبت شده است؛ فشار کمبود وجود دارد`,
            stockDirections: {
                building: 'موجودی در حال افزایش است',
                drawing: 'موجودی در حال کاهش است',
                steady: 'موجودی تقریباً ثابت است',
            },
        },
        pressures: {
            shortage_pressure: 'فشار کمبود',
            surplus: 'مازاد و رشد موجودی',
            neutral: 'بدون نشانهٔ روشن',
        },
        balanceBands: {
            surplus: 'عرضه بیشتر از فروش',
            balanced: 'در محدودهٔ توازن ±۱۰٪',
            shortage: 'فروش بیشتر از عرضه',
            unavailable: 'شاخص نامشخص',
        },
        stockDirections: {
            building: 'موجودی در حال افزایش',
            drawing: 'موجودی در حال کاهش',
            steady: 'موجودی بدون تغییر',
        },
        series: {
            supply_kg: 'عرضهٔ بالادستی',
            sales_kg: 'فروش به بازار',
            stock_kg: 'موجودی تجمعی',
            sale_price_per_kg: 'قیمت فروش کارخانه',
            retail_price_per_kg: 'قیمت خرده‌فروشی',
        },
        negativeStock: 'مقدار تجمعی فروش از عرضهٔ ثبت‌شده بیشتر شده است؛ موجودی در نما صفر نشان داده می‌شود.',
        noData: 'برای این محصول و بازه داده‌ای وجود ندارد.',
        error: 'بارگذاری توازن عرضه و تقاضا ناموفق بود.',
    },

    /** `units` object in the payload: quantity_unit / price_unit / … */
    units: {
        kg: 'کیلوگرم',
        t: 'تن',
        toman: 'تومان',
        rial: 'ریال',
    },

    kpis: {
        chains: 'زنجیره‌های ردیابی‌شده',
        critical: 'هشدارهای بحرانی',
        warning: 'هشدارهای متوسط',
        hoarding: 'مظنونان به احتکار',
        coverage: 'میانگین پوشش تأییدشده',
        sources: 'منبع داده در دسترس',
        sourcesUnavailable: (count) => `${formatNumber(count)} منبع در دسترس نیست`,
        unavailableSources: (names) => `منابع دادهٔ در دسترس نیست: ${names}`,
    },

    charts: {
        retention: {
            title: 'مقدار باقی‌مانده در هر مرحله (درصد مقدار سفارش‌شده)',
            avg: 'میانگین همه زنجیره‌ها',
            note: 'افت نمودار یعنی بخشی از مقدار بین دو مرحله نامشخص شده است (قانون R۱).',
            reference: 'مقدار پایه · ۱۰۰٪',
        },
        ladder: {
            title: 'نردبان قیمت مراحل در برابر قیمت مشتری نهایی',
            avg: 'میانگین همه زنجیره‌ها',
            note: 'نقطهٔ آخر میانگین قیمت در پلتفرم‌های تورب، دیجی‌کالا و باسلام است.',
        },
        hoarding: {
            title: 'احتکار: فروخته‌شده در برابر انبارش‌شده (تن)',
            sold: 'فروخته‌شده در بازار',
            warehoused: 'انبارش‌شده',
        },
        trend: {
            title: 'قیمت فروش کارخانه در برابر میانگین خرده‌فروشی (هفتگی)',
            factory: 'قیمت فروش کارخانه',
            retail: 'میانگین خرده‌فروشی',
            noFactorySales: 'بدون فروش کارخانه',
            noFactoryHistory: 'برای این بازه داده‌ای از فروش کارخانه ثبت نشده است.',
            priceGap: 'اختلاف قیمت خرده‌فروشی و کارخانه',
            gapMarkerNote: 'نشان × یعنی در آن هفته فروش کارخانه ثبت نشده است.',
        },
        alertsByRule: 'هشدارها به تفکیک قانون',
        chain: 'کاوشگر زنجیره',
        chainSelect: 'انتخاب زنجیره',
        alerts: {
            title: 'هشدارها',
            hint: 'برای باز کردن زنجیره کلیک کنید',
            empty: 'هشداری ثبت نشده است.',
            search: 'جست‌وجو در متن هشدار',
            allRules: 'همهٔ قواعد',
            chain: 'زنجیره',
            allChains: 'همهٔ زنجیره‌ها',
            unlinked: 'بدون زنجیره',
            sort: 'مرتب‌سازی',
            sortSeverity: 'شدت، سپس جدیدترین',
            sortDate: 'جدیدترین',
            sortRule: 'قاعده',
            clear: 'پاک‌کردن همهٔ فیلترها',
            resultCount: (count) => `${formatNumber(count)} هشدار`,
            noResults: 'هشداری با این فیلترها پیدا نشد.',
            exportCsv: 'دریافت CSV',
            print: 'چاپ گزارش',
            page: (current, total) => `صفحهٔ ${formatNumber(current)} از ${formatNumber(total)}`,
            previous: 'صفحهٔ قبل',
            next: 'صفحهٔ بعد',
            groupCoverage: (count) => `${formatNumber(count)} زنجیره با ردیابی ناقص`,
            groupRule: (rule, count) => `${formatNumber(count)} زنجیره · ${rule}`,
            groupUnlinked: (count) => `${formatNumber(count)} هشدار تکراری`,
            groupToggle: (expanded) => expanded ? 'جمع‌کردن گروه' : 'نمایش هشدارها',
            sortFilter: (value) => `مرتب‌سازی: ${value}`,
            date: 'تاریخ',
            severity: 'شدت',
            rule: 'قاعده',
            commodity: 'کالا',
            chainId: 'شناسهٔ زنجیره',
            message: 'پیام',
            evidence: 'شواهد',
            generatedAt: (date) => `تاریخ تهیهٔ گزارش: ${date}`,
            activeFilters: 'فیلترهای فعال',
            allSeverities: 'همهٔ شدت‌ها',
            searchFilter: (value) => `متن: ${value}`,
            chainFilter: (value) => `زنجیره: ${value}`,
            severityFilter: (value) => `شدت: ${value}`,
            ruleFilter: (value) => `قاعده: ${value}`,
            exportRule: (name, code) => `${name} (${code})`,
            previousPage: 'قبلی',
            nextPage: 'بعدی',
        },
        sources: {
            title: 'منابع داده · میزان تازگی',
            fresh: (hours) => `${persianDigits(hours)} ساعت پیش دریافت شده`,
            stale: (hours) => `غیرقابل دسترسی یا قدیمی · ${persianDigits(hours)} ساعت`,
        },
    },

    chain: {
        coverage: 'پوشش تأییدشده',
        lastVerified: 'آخرین مرحلهٔ تأییدشده',
        noDate: 'بدون تاریخ',
        noSourceData: 'داده‌ای از این منبع دریافت نشده است',
        endCustomer: 'مشتری نهایی',
        endCustomerSource: 'میانگین پلتفرم · تورب / دیجی‌کالا / باسلام',
        selectedChain: 'زنجیرهٔ انتخاب‌شده',
        numberedChain: (number) => `زنجیرهٔ ${formatNumber(number)}`,
        qty: 'کیلوگرم',
        perKg: 'تومان / کیلوگرم',
        graph: {
            viewLabel: 'شیوهٔ نمایش زنجیره',
            viewGraph: 'نمودار',
            viewList: 'فهرست',
            loading: 'در حال بارگذاری نمودار زنجیره…',
            error: 'بارگذاری نمودار زنجیره ناموفق بود.',
            unavailable: 'نمودار این زنجیره در دسترس نیست.',
            detailsTitle: 'جزئیات گره',
            selectNode: 'برای دیدن سند، منبع، تاریخ و پرچم‌ها یک گره را انتخاب کنید.',
            noDetails: 'جزئیات تکمیلی برای این گره ثبت نشده است.',
            source: 'منبع',
            document: 'شمارهٔ سند',
            date: 'تاریخ',
            nationalId: 'شناسهٔ ملی',
            productKey: 'کلید محصول',
            quantity: 'مقدار',
            price: 'قیمت',
            batches: 'بسته‌ها',
            locations: 'مکان‌ها',
            flags: 'پرچم‌ها',
            note: 'توضیح',
            quantityChange: 'تغییر مقدار',
            priceChange: 'تغییر قیمت',
            days: 'روز',
            locationTypes: {
                origin_country: 'کشور مبدأ',
                warehouse_location: 'انبار',
                destination_province: 'استان مقصد',
                facility_location: 'کشتارگاه',
            },
            states: {
                verified: 'تأییدشده',
                expected: 'مقدار مورد انتظار',
                unverified: 'تأییدنشده',
                missing: 'ثبت‌نشده',
                flagged: 'پرچم‌دار',
            },
            unknownNode: 'جزئیات مرحله',
            registered: 'ثبت‌شده',
        },
    },

    /** Rule codes → Persian names (alert_rules.py). */
    rules: {
        R1: 'اختلاف مقدار بین مراحل',
        R1b: 'حجم غیرواقعی',
        R4: 'نظم زمانی مراحل',
        R5: 'مشکل گواهی',
        R6: 'وقفه در زنجیرهٔ مالکیت',
        R7: 'فراتر از ظرفیت مجاز',
        R8: 'جهش قیمت',
        R9: 'احتکار',
        R10: 'سلامت منبع داده',
        COV: 'پوشش تأییدشده',
    },

    /** Payload severities critical/warning/info → بالا/متوسط/پایین. */
    severity: {
        critical: 'بالا',
        warning: 'متوسط',
        info: 'پایین',
    },

    empty: {
        chart: 'داده‌ای برای نمایش این نمودار وجود ندارد.',
        sources: 'هیچ منبع داده‌ای ثبت نشده است.',
    },

    commodityLabel: (key) => getPersianLabel(fa.commodities, key, 'traceability.commodities'),
    unitLabel: (unit) => getPersianLabel(fa.units, unit, 'traceability.units'),
    ruleName: (code) => getPersianLabel(fa.rules, code, 'traceability.rules'),
    severityLabel: (severity) => getPersianLabel(fa.severity, severity, 'traceability.severity'),
    sourceName: (name) => getPersianLabel(fa.sources, name, 'traceability.sources'),

    /**
     * Stage label by the stable stage `code`. Message-template slots carry these
     * codes, so this one lookup serves the alerts and the charts.
     */
    stageName: (code) => getPersianLabel(fa.stages, code, 'traceability.stages'),

    /** One `{slot}` of a message template: translate codes, format numbers. */
    formatParam: (key, value, units) => {
        if (value === null || value === undefined) return '';
        if (STAGE_PARAMS.includes(key)) return fa.stageName(value);
        if (key === 'source') return fa.sourceName(value);
        if (key === 'result') return getPersianLabel(fa.inspectionResult, value, 'traceability.inspectionResult');
        if (key === 'holder') return persianDisplayText(value, 'فروشنده');
        if (key === 'kg') return `${formatNumber(value)} ${fa.unitLabel(units?.quantity_unit)}`;
        if (key === 'pct' || key === 'share') return `${formatNumber(value)}٪`;
        if (typeof value === 'number') return formatNumber(value);
        return persianDisplayText(persianDigits(value), 'نامشخص');
    },

    /**
     * One Persian sentence per `message_code` (see alert_rules.MESSAGE_CODES).
     * `{slot}` is filled from `message_params`; a function template gets the raw
     * params for the cases one sentence shape cannot cover.
     */
    messages: {
        qty_unaccounted: '{kg} از مرحلهٔ {from_stage} تا مرحلهٔ {to_stage} بدون سند ثبت شده است',
        phantom_volume: '{kg} در مرحلهٔ {to_stage} بیشتر از مرحلهٔ {from_stage} ثبت شده است',
        date_out_of_order: 'تاریخ مرحلهٔ {stage} قبل از تاریخ مرحلهٔ {previous_stage} است',
        inspection_failed: (p, resolve) => p.result
            ? `نتیجهٔ گواهی دامپزشکی ${resolve('result')} است؛ بار همچنان به مراحل پایین‌دست ارسال شده است`
            : 'گواهی دامپزشکی پاس نشده است؛ بار همچنان به مراحل پایین‌دست ارسال شده است',
        label_mismatch: 'برچسب محصول با گواهی دامپزشکی همخوانی ندارد؛ بار به مراحل پایین‌دست ارسال شده است',
        sampling_missing: 'نمونه‌گیری دامپزشکی انجام نشده است؛ بار به مراحل پایین‌دست ارسال شده است',
        ownership_break: 'زنجیرهٔ مالکیت قطع شده است: فروشنده یا خریدار نهایی با مالک قبلی همخوانی ندارد',
        capacity_exceeded: 'تعداد جوجه‌ها ({chick_count}) از ظرفیت مجاز مزرعه ({capacity}) بیشتر است',
        jump_step: 'قیمت در مرحلهٔ {to_stage} نسبت به مرحلهٔ {from_stage} {pct} افزایش یافته است',
        price_above_retail: 'قیمت فروش در بازار بالاتر از میانگین قیمت مشتری نهایی است',
        hoarding: '{holder} در حال نگهداری {share} از تولید خود در انبار، به مدت {days} روز است',
        source_never_fetched: 'هیچ داده‌ای از {source} دریافت نشده است',
        source_stale: '{source} غیرقابل دسترسی یا قدیمی است ({hours} ساعت از آخرین دریافت گذشته)',
        chain_stops: 'ردیابی تأییدشده در مرحلهٔ {stage} متوقف می‌شود',
        chain_stops_start: 'هیچ مرحله‌ای از این زنجیره تأیید نشده است',
    },

    /** InspectionResult values as the API sends them (base.InspectionResult). */
    inspectionResult: {
        passed: 'پاس شده',
        failed: 'رد شده',
        pending: 'در انتظار',
    },

    sources: {
        NTSW: 'سامانه جامع تجارت',
        Customs: 'گمرک',
        IVO: 'سازمان دامپزشکی',
        'Warehouse docs': 'اسناد انبار',
        'Livestock support': 'پشتیبانی دام (قیمت پایه)',
        'Voucher system': 'سامانه کوپن',
        'Transfer records': 'سوابق انتقال مالکیت',
        'Factory batch': 'بستهٔ تولید کارخانه',
        'Market movement': 'عرضه در بازار',
        'Samast / Trace': 'سماصط / ردیابی',
        Bazargah: 'بازارگاه',
        'Vet Trace': 'ردیابی دامپزشکی',
        'Poultry tracking': 'ردیابی طیور',
        'Retail scrapers': 'جمع‌آوری قیمت خرده‌فروشی',
    },

    stages: {
        order: 'ثبت سفارش',
        customs: 'ترخیص گمرکی',
        vet: 'معاینه دامپزشکی',
        warehouse: 'رسید انبار',
        price_approved: 'تأیید قیمت پایه',
        voucher: 'کوپن توزیع',
        transfer_1: 'انتقال مالکیت ۱',
        transfer_2: 'انتقال مالکیت ۲',
        packaged: 'بستهٔ تولید کارخانه',
        market: 'عرضه در بازار',
        placement: 'جوجه‌ریزی',
        feed: 'خرید خوراک',
        transport_cert: 'گواهی حمل',
        slaughter: 'تحویل به کشتارگاه',
        distribution: 'سوابق توزیع',
        end_customer: 'مشتری نهایی (میانگین)',
    },
};

/**
 * Turn an alert's `message_code` + `message_params` into a Persian sentence.
 */
export const renderMessage = (messageCode, params, units) => {
    const template = fa.messages[messageCode];
    if (!template) {
        if (import.meta.env.DEV) {
            console.warn(`[i18n] Missing Persian translation: traceability.messages.${messageCode ?? '(empty)'}`);
        }
        return 'هشدار تازه‌ای ثبت شده است.';
    }
    const resolve = (key) => fa.formatParam(key, params?.[key], units);
    if (typeof template === 'function') return template(params ?? {}, resolve);
    return template.replace(/\{(\w+)\}/g, (_, key) => resolve(key));
};

export default fa;
